import { Grid, Typography, Button } from '@mui/material';
import { Suggestion } from '@pepperhq/menu-sdk';
import { Form } from 'formik';
import { ProductSuggestionType } from '../model/menu';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { ImageFormField } from 'lib/form/fields/ImageFormField';
import { AutocompleteFormField } from 'lib/form/fields/AutocompleteFormField';
import { MuiForm } from 'lib/Form';
import { Option } from 'lib/types';
import { Row } from 'ui/Flex';
import React from 'react';
import * as yup from 'yup';

interface SuggestionFormProps {
    productOptions: Option[];
    suggestion?: Suggestion;
    onClose: () => void;
    onSave: (suggestion: Suggestion) => void;
    onRemove: () => void;
    bogofExists: boolean;
}

const typeOptions: Array<{ value: ProductSuggestionType; label: string }> = [
    { value: 'add', label: 'ADD' },
    { value: 'bogof', label: 'OFFER' },
    { value: 'replace', label: 'REPLACE' }
];

const schema = yup.object().shape({
    title: yup.string().required('This field is required'),
    description: yup.mixed().when('type', {
        is: (target: string) => target === 'bogof',
        then: yup.mixed().required('This field is required.'),
        otherwise: undefined
    }),
    type: yup
        .string()
        .oneOf(typeOptions.map(option => option.value))
        .required('This field is required.'),
    productId: yup.mixed().when('type', {
        is: (target: string) => target === 'add' || target === 'replace',
        then: yup.mixed().required('This field is required.'),
        otherwise: undefined
    })
});

export const SuggestionsForm: React.FC<SuggestionFormProps> = ({
    suggestion,
    productOptions,
    bogofExists,
    onClose,
    onSave,
    onRemove
}) => {
    // Can only have one BOGOF suggestion
    const calculatedTypeOptions = React.useMemo(() => {
        if (bogofExists && suggestion?.type !== 'bogof') {
            return typeOptions.filter(option => option.value !== 'bogof');
        }
        return typeOptions;
    }, [bogofExists, suggestion?.type]);
    const formTitle = React.useMemo(
        () => (!!suggestion ? 'Edit Product Suggestion' : 'Add Product Suggestion'),
        [suggestion]
    );
    const handleSubmit = React.useCallback(
        (values: Suggestion) => {
            if (values.type === 'bogof') {
                const value: Suggestion = { ...values, productId: undefined };
                onSave(value);
                onClose();
                return;
            }
            onSave(values);
            onClose();
        },
        [onClose, onSave]
    );

    const handleRemove = React.useCallback(() => {
        onRemove();
        onClose();
    }, [onRemove, onClose]);

    const initialValues = React.useMemo(
        () =>
            suggestion ?? {
                title: '',
                description: '',
                type: '',
                productId: '',
                imageUrl: ''
            },
        [suggestion]
    );

    return (
        <MuiForm onSubmit={handleSubmit} initialValues={initialValues} validationSchema={schema}>
            {({ submitForm, values }) => (
                <Form>
                    <Grid container spacing={2} justifyContent="space-between">
                        <Grid
                            size={{
                                sm: 12
                            }}
                        >
                            <Typography variant="h6" color="primary">
                                {formTitle}
                            </Typography>
                        </Grid>
                        <Grid
                            container
                            spacing={1}
                            size={{
                                xs: 12,
                                sm: 6
                            }}
                        >
                            <Grid size={12}>
                                <SelectFormField name="type" options={calculatedTypeOptions} label="Type" />
                            </Grid>

                            {(values?.type === 'add' || values?.type === 'replace') && (
                                <Grid size={12}>
                                    <AutocompleteFormField
                                        name="productId"
                                        label="Product"
                                        options={productOptions}
                                    />
                                </Grid>
                            )}

                            <Grid size={12}>
                                <TextFormField label="Title" name="title" />
                            </Grid>
                        </Grid>
                        <Grid
                            container
                            spacing={1}
                            size={{
                                xs: 12,
                                sm: 6
                            }}
                        >
                            <Grid size={12}>
                                <TextFormField name="description" label="Description" multiline />
                            </Grid>
                            <Grid size={12}>
                                <ImageFormField
                                    aspect={3.5}
                                    name="imageUrl"
                                    description="600px by 200px. Max. file size is 1MB."
                                    smallText
                                />
                            </Grid>
                        </Grid>
                        <Grid
                            size={{
                                sm: 12
                            }}
                        >
                            <Row align="space-between" flex={1}>
                                <Row flex={1}>
                                    {!!values && !!suggestion && (
                                        <Button onClick={handleRemove} color="primary">
                                            Remove Suggestion
                                        </Button>
                                    )}
                                </Row>
                                <Row gutter>
                                    <Button color="primary" onClick={onClose}>
                                        Cancel
                                    </Button>
                                    <Button variant="contained" color="primary" onClick={submitForm}>
                                        Save
                                    </Button>
                                </Row>
                            </Row>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </MuiForm>
    );
};
